import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import { judgeRange, missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Eye-open check.
 *
 * Two sources, in order of trust:
 *  1. `blendshapes.eyeBlinkLeft/Right` from the model — near-binary and reliable;
 *  2. the geometric eye-aspect-ratio (eyelid gap / eye width) as a fallback.
 *
 * Squinting and heavy eyeliner can fool the geometric fallback, which is why it
 * reports a lower confidence and the reason is spelled out in `details`.
 */
export const eyeOpenCheck: ValidationCheck = {
  id: 'eye_open',
  title: 'Глаза открыты',
  description: 'Проверяет, что глаза открыты и не прищурены.',
  kind: 'content',
  requires: ['geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['geometry']);
    if (missing) return notChecked(ctx, eyeOpenCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    if (!geometry) return notChecked(ctx, eyeOpenCheck, 'Геометрия недоступна.', ctx.rule.severity);

    const minEar = numberParam(ctx.rule.params, 'minEyeAspectRatio', 0.17);
    const blinkThreshold = numberParam(ctx.rule.params, 'blinkThreshold', 0.5);

    const shapes: NonNullable<CheckResult['geometry']> = [
      { type: 'point', coordinates: [roundTo(geometry.eyeLeft.x, 4), roundTo(geometry.eyeLeft.y, 4)] },
      { type: 'point', coordinates: [roundTo(geometry.eyeRight.x, 4), roundTo(geometry.eyeRight.y, 4)] },
    ];

    const blendshapes = ctx.faces.faces[0]?.blendshapes;
    if (blendshapes) {
      const blinkLeft = blendshapes.eyeBlinkLeft ?? 0;
      const blinkRight = blendshapes.eyeBlinkRight ?? 0;
      const closed = Math.max(blinkLeft, blinkRight);
      const status = closed > blinkThreshold ? 'fail' : closed > blinkThreshold * 0.75 ? 'warning' : 'pass';
      return plainResult({
        id: eyeOpenCheck.id,
        title: eyeOpenCheck.title,
        status,
        severity: ctx.rule.severity,
        value: roundTo(closed, 3),
        max: blinkThreshold,
        message:
          status === 'pass'
            ? 'Оба глаза открыты.'
            : status === 'warning'
              ? 'Один глаз прищурен — на документных фото глаза должны быть широко открыты.'
              : 'Похоже, один или оба глаза закрыты.',
        requirement: 'Глаза открыты, взгляд направлен в объектив.',
        fixability: 'retake',
        geometry: shapes,
        confidence: 0.9,
        details: { blinkLeft, blinkRight, source: 'blendshapes', displayValue: `${roundTo(closed, 2)}` },
      });
    }

    const ear = geometry.eyeAspectRatio.min;
    const verdict = judgeRange({
      value: ear,
      min: minEar,
      warningMargin: 0.25,
      confidence: 0.55,
      uncertainBelow: 0.25,
    });

    return plainResult({
      id: eyeOpenCheck.id,
      title: eyeOpenCheck.title,
      status: verdict.status,
      severity: ctx.rule.severity,
      value: roundTo(ear, 3),
      min: minEar,
      unit: 'ratio',
      message:
        verdict.status === 'pass'
          ? 'Глаза открыты.'
          : verdict.status === 'uncertain'
            ? 'Степень открытости глаз определить неуверенно — проверьте фото визуально.'
            : 'Похоже, один или оба глаза прикрыты.',
      requirement: 'Глаза открыты, взгляд направлен в объектив.',
      fixability: 'retake',
      geometry: shapes,
      confidence: 0.55,
      details: {
        eyeAspectRatioLeft: geometry.eyeAspectRatio.left,
        eyeAspectRatioRight: geometry.eyeAspectRatio.right,
        minEyeAspectRatio: minEar,
        source: 'geometric',
        displayValue: `${roundTo(ear, 2)}`,
      },
    });
  },
};
